import {parseImpersonationData} from '@/modules/auth/functions'
import {TIMELINE_ICONS} from '@/modules/system-logs/constants'
import {getLogDomain} from '@/modules/system-logs/functions'
import {drawerDetails, formatLogItem, parseLogItems} from '@/modules/system-logs/log-message'
import {useEventLogContext} from '@/shared/contexts/event-log'
import {Avatar, Badge, Box, Flex, Icon, Text, Timeline} from '@chakra-ui/react'
import {EventLog} from '@cypro/engine/schema'
import {Tooltip} from '@cypro/ui/components'
import {toPascalCase} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {isNamedRefKey, renderDetailBadge} from './details-renderer'

export function LogDrawerTimeline() {
  const {t} = useTranslate('common')
  const {log} = useEventLogContext()
  const impersonationData = parseImpersonationData(log?.impersonatedBy)
  const actorName = log.actor?.displayUsername || log.actor?.username || log.actor?.name || t('system')

  return (
    <Box bg="gray.50" border="1px solid" borderColor="gray.200" rounded="lg" p={5}>
      <Timeline.Root>
        <Timeline.Item>
          <Timeline.Connector>
            <Timeline.Separator />
            <Timeline.Indicator>
              <Avatar.Root size="xs" data-e2e="current-user">
                <Avatar.Fallback name={actorName} />
              </Avatar.Root>
            </Timeline.Indicator>
          </Timeline.Connector>
          <Timeline.Content pb={10} wordBreak="break-all" whiteSpace="pre-line">
            <Text fontSize="sm">{actorName}</Text>
            <Text fontSize="sm" color="fg.muted">
              {log.actor?.role || ''}
            </Text>
            {impersonationData && (
              <Box as="p" colorPalette="red" maxWidth="max-content">
                <Tooltip content={t('impersonated_by', {adminName: impersonationData.adminName})}>
                  <span>{t('impersonated_by', {adminName: impersonationData.adminName})}</span>
                </Tooltip>
              </Box>
            )}
          </Timeline.Content>
        </Timeline.Item>

        <Timeline.Item>
          <Timeline.Connector>
            <Timeline.Separator />
            <Timeline.Indicator>
              <Avatar.Root size="xs" data-e2e="current-user">
                <Icon as={renderIcon(getLogDomain(log))} boxSize={3} />
              </Avatar.Root>
            </Timeline.Indicator>
          </Timeline.Connector>
          <ResourceItemContent log={log} />
        </Timeline.Item>
      </Timeline.Root>
    </Box>
  )
}

function renderIcon(domain: string | null) {
  if (domain) {
    const key = domain as keyof typeof TIMELINE_ICONS
    return TIMELINE_ICONS[key] ?? TIMELINE_ICONS.misc
  }
  return TIMELINE_ICONS.misc
}

function ResourceItemContent({log}: {log: EventLog}) {
  const {t} = useTranslate('common')

  if (log.domain === 'hl7')
    return (
      <Timeline.Content>
        <Text fontSize="sm">{t('log_HL7_integration_interface')}</Text>
        <Text fontSize="sm" color="fg.muted">
          {t('log_integration')}
        </Text>
      </Timeline.Content>
    )

  if (log.resource) {
    const details = drawerDetails(log.details, isNamedRefKey)
    const items = parseLogItems(log.details)

    return (
      <Timeline.Content>
        <Flex gap={2} flexWrap="wrap">
          <Text fontSize="sm">{t(`resource.${log.resource}`)}</Text>

          {details.map(([key, value]) => {
            const badge = renderDetailBadge(key, value)
            if (!badge) return null
            return (
              <Tooltip key={key} content={toPascalCase(key)}>
                {badge}
              </Tooltip>
            )
          })}

          {items.map(item => (
            <Badge key={JSON.stringify(item)} variant="outline" backgroundColor="white" whiteSpace="pre-line">
              {formatLogItem(item, t)}
            </Badge>
          ))}
        </Flex>

        <Text fontSize="sm" color="fg.muted">
          {t('log_affected_resource')}
        </Text>
      </Timeline.Content>
    )
  }

  return (
    <Timeline.Content>
      <Text fontSize="sm">{t('system')}</Text>
    </Timeline.Content>
  )
}
